import { getLanguage as getInterfaceLanguage } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MoreVertical,
  Settings,
  Code2,
  Trash2,
  BarChart3,
  Play,
  FolderOpen,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  Download,
  FileEdit,
  Share2
} from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { BulkActionsBar } from '@/components/hierarchical-folders/BulkActionsBar';
import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { cn } from '@/lib/utils';
import { Video } from '@/types/hierarchicalFolders';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { formatDate } from '@/utils/formatDate';
import { DeleteVideoModal } from '@/components/shared/DeleteVideoModal';
import { RenameVideoModal } from '@/components/shared/RenameVideoModal';
import { CopyCodeModal } from '@/components/shared/CopyCodeModal';
import { downloadVideo, downloadVideoDirectLink } from '@/utils/downloadVideo';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { shareVideoService } from '@/services/shareVideoService';
import { videoService } from '@/services/videoService';
import { DuplicateVideoMenuItem } from '@/components/shared/DuplicateVideoMenuItem';
import { VideoActionsDropdown } from '@/components/shared/VideoActionsDropdown';

interface VideoTablePremiumProps {
  videos: Video[];
  onVideoClick?: (video: Video) => void;
  onVideoEdit?: (video: Video) => void;
  onVideoCopyCode?: (video: Video) => void;
  onVideoDelete?: (video: Video) => void;
  onVideoRename?: (videoId: string, newTitle: string) => void;
  onBulkDelete?: (videoIds: string[]) => void;
  onVideoStats?: (video: Video) => void;
  /**
   * Ordenação vinda do dropdown "Ordenar por" do dashboard. Quando muda, a
   * tabela passa a ordenar pela mesma métrica/direção. Clicar num cabeçalho
   * continua funcionando e sobrescreve localmente até o dropdown mudar de novo.
   */
  sortOption?: { metric: 'plays' | 'views'; order: 'desc' | 'asc' };
}

interface ColumnHeaderProps {
  label: string;
  sortActive: boolean;
  sortDirection: 'asc' | 'desc';
  onSort: () => void;
}

function ColumnHeader({ label, sortActive, sortDirection, onSort }: ColumnHeaderProps) {
  useLanguage();
  return (
    <button
      onClick={onSort}
      className={cn(
        "flex items-center gap-1.5 transition-colors whitespace-nowrap cursor-pointer hover:text-[#3478F6]",
        sortActive ? "text-[#3478F6] font-semibold" : "text-[#667085]"
      )}
    >
      <span>{label}</span>
      {sortActive ? (
        sortDirection === 'asc' ?
        <ArrowUp className="h-3.5 w-3.5 text-[#3478F6]" strokeWidth={2.5} /> :
        <ArrowDown className="h-3.5 w-3.5 text-[#3478F6]" strokeWidth={2.5} />
      ) : (
        <ArrowUpDown className="h-3.5 w-3.5 opacity-25" />
      )}
    </button>
  );
}

interface DraggableTableRowProps {
  video: Video;
  children: React.ReactNode;
}

function DraggableTableRow({ video, children }: DraggableTableRowProps) {
  useLanguage();
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `video-${video.id}`,
    data: {
      type: 'video',
      videoId: video.id,
      videoTitle: video.title,
      currentFolderId: null,
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    opacity: isDragging ? 0.5 : 1,
    cursor: isDragging ? 'grabbing' : 'grab',
  };

  return (
    <TableRow 
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={cn(
        "border-b border-[#EDF0F4] hover:bg-[#FAFBFD] transition-all duration-200",
        isDragging && "shadow-2xl scale-105 bg-primary/10 border-primary z-50"
      )}
    >
      {children}
    </TableRow>
  );
}

// Formata valor monetário baseado na moeda
const formatCurrency = (value: number, currency: 'BRL' | 'USD' = 'BRL') => {
  if (currency === 'USD') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  }
  
  return new Intl.NumberFormat(getInterfaceLanguage(), {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

export function VideoTablePremium({
  videos,
  onVideoClick,
  onVideoEdit,
  onVideoCopyCode,
  onVideoDelete,
  onVideoRename,
  onBulkDelete,
  onVideoStats,
  sortOption,
}: VideoTablePremiumProps) {
  useLanguage();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sortColumn, setSortColumn] = useState<'createdAt' | 'updatedAt' | 'views' | 'plays' | 'conversions' | 'revenue' | null>(sortOption?.metric ?? 'plays');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(sortOption?.order ?? 'desc');

  // Acompanha o dropdown do dashboard. Depende dos campos (e não do objeto),
  // que é recriado a cada render do pai e reverteria a ordenação por cabeçalho.
  useEffect(() => {
    if (!sortOption) return;
    setSortColumn(sortOption.metric);
    setSortDirection(sortOption.order);
  }, [sortOption?.metric, sortOption?.order]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [copyCodeModalOpen, setCopyCodeModalOpen] = useState(false);
  const [videoToDelete, setVideoToDelete] = useState<Video | null>(null);
  const [videoToRename, setVideoToRename] = useState<Video | null>(null);
  const [videoToCopy, setVideoToCopy] = useState<Video | null>(null);
  const [selectedVideoIds, setSelectedVideoIds] = useState<Set<string>>(new Set());
  const [bulkDeleteModalOpen, setBulkDeleteModalOpen] = useState(false);

  // Funções de seleção múltipla
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = new Set(processedVideos.map(v => v.id));
      setSelectedVideoIds(allIds);
    } else {
      setSelectedVideoIds(new Set());
    }
  };

  const handleSelectVideo = (videoId: string, checked: boolean) => {
    setSelectedVideoIds(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(videoId);
      } else {
        newSet.delete(videoId);
      }
      return newSet;
    });
  };

  const handleClearSelection = () => {
    setSelectedVideoIds(new Set());
  };

  const processedVideos = useMemo(() => {
    if (sortColumn) {
      const sorted = [...videos].sort((a, b) => {
        const multiplier = sortDirection === 'asc' ? 1 : -1;

        if (sortColumn === 'createdAt') {
          return multiplier * (new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
        } else if (sortColumn === 'updatedAt') {
          return multiplier * (new Date(a.updatedAt || a.createdAt).getTime() - new Date(b.updatedAt || b.createdAt).getTime());
        } else if (sortColumn === 'views') {
          return multiplier * ((a.views || 0) - (b.views || 0));
        } else if (sortColumn === 'plays') {
          return multiplier * ((a.plays || 0) - (b.plays || 0));
        } else if (sortColumn === 'conversions') {
          return multiplier * ((a.conversions || 0) - (b.conversions || 0));
        } else if (sortColumn === 'revenue') {
          return multiplier * ((a.revenue || 0) - (b.revenue || 0));
        }
        return 0;
      });
      return sorted;
    }

    return videos;
  }, [videos, sortColumn, sortDirection]);

  const isAllSelected = processedVideos.length > 0 && 
    processedVideos.every(v => selectedVideoIds.has(v.id));
  const isPartiallySelected = processedVideos.some(v => selectedVideoIds.has(v.id)) && !isAllSelected;

  const handleColumnSort = (column: 'createdAt' | 'updatedAt' | 'views' | 'plays' | 'conversions' | 'revenue') => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  return (
    <div className="space-y-4">
      <div className="border border-[#EEF2F7] rounded-[10px] overflow-hidden bg-white">
        <div className="overflow-x-auto max-h-[600px] premium-scroll">
          <Table className="min-w-[800px]">
        <TableHeader>
          <TableRow className="bg-[#F8FAFD] border-b border-[#EDF0F4] hover:bg-[#F8FAFD] h-[42px]">
            <TableHead className="font-semibold text-foreground/80 text-[13px] w-[40px] pr-0">
              <Checkbox
                checked={isAllSelected ? true : isPartiallySelected ? "indeterminate" : false}
                onCheckedChange={(checked) => handleSelectAll(!!checked)}
              />
            </TableHead>
            <TableHead className="font-semibold text-[#667085] text-[12px] w-[23%]">
              {translateInterfaceText("Nome")}</TableHead>
            
            <TableHead className="font-semibold text-[#667085] text-[12px] w-[11%] whitespace-nowrap">
              <ColumnHeader
                label={translateInterfaceText("Criado em")}
                sortActive={sortColumn === 'createdAt'}
                sortDirection={sortDirection}
                onSort={() => handleColumnSort('createdAt')}
              />
            </TableHead>

            <TableHead className="font-semibold text-[#667085] text-[12px] w-[11%] whitespace-nowrap">
              <ColumnHeader
                label={translateInterfaceText("Última edição")}
                sortActive={sortColumn === 'updatedAt'}
                sortDirection={sortDirection}
                onSort={() => handleColumnSort('updatedAt')}
              />
            </TableHead>

            <TableHead className="font-semibold text-[#667085] text-[12px] w-[11%]">
              <ColumnHeader
                label={translateInterfaceText("Visualizações")}
                sortActive={sortColumn === 'views'}
                sortDirection={sortDirection}
                onSort={() => handleColumnSort('views')}
              />
            </TableHead>
            
            <TableHead className="font-semibold text-[#667085] text-[12px] w-[10%]">
              <ColumnHeader
                label={translateInterfaceText("Plays")}
                sortActive={sortColumn === 'plays'}
                sortDirection={sortDirection}
                onSort={() => handleColumnSort('plays')}
              />
            </TableHead>

            <TableHead className="font-semibold text-[#667085] text-[12px] w-[12%]">
              <ColumnHeader
                label={translateInterfaceText("Conversões")}
                sortActive={sortColumn === 'conversions'}
                sortDirection={sortDirection}
                onSort={() => handleColumnSort('conversions')}
              />
            </TableHead>

            <TableHead className="font-semibold text-[#667085] text-[12px] w-[12%]">
              <ColumnHeader
                label={translateInterfaceText("Receita")}
                sortActive={sortColumn === 'revenue'}
                sortDirection={sortDirection}
                onSort={() => handleColumnSort('revenue')}
              />
            </TableHead>
            
            <TableHead className="font-semibold text-[#667085] text-[12px] text-right w-[10%]">
              {translateInterfaceText("Ações")}</TableHead>
          </TableRow>
        </TableHeader>
          <TableBody>
            {processedVideos.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="h-24 text-center">
                  <div className="text-muted-foreground">
                    {translateInterfaceText("Nenhum vídeo disponível")}</div>
                </TableCell>
              </TableRow>
            ) : (
              processedVideos.map((video) => (
                <DraggableTableRow key={video.id} video={video}>
                  <TableCell className="pr-0 py-2" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={selectedVideoIds.has(video.id)}
                      onCheckedChange={(checked) => handleSelectVideo(video.id, !!checked)}
                    />
                  </TableCell>
                  <TableCell 
                    className="font-medium cursor-pointer hover:text-[#3F6FE5] transition-colors max-w-[300px] py-2"
                    onClick={() => onVideoClick?.(video)}
                  >
                    <div className="flex items-center gap-3">
                      {/* Thumbnail 32x32 */}
                      <div className="relative w-8 h-8 bg-black/[0.02] rounded-[6px] overflow-hidden flex-shrink-0 border border-[#EDF0F4]">
                        {video.thumbnailUrl ? (
                          <img 
                            src={video.thumbnailUrl} 
                            alt={video.title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Play className="h-3 w-3 text-[#9CA3AF]" />
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col min-w-0">
                        <span className="text-[13px] font-medium text-foreground/90 truncate">
                          {video.title}
                        </span>
                      </div>
                    </div>
                  </TableCell>

                  <TableCell className="text-[13px] text-muted-foreground py-2">
                    {formatDate(video.createdAt)}
                  </TableCell>

                  <TableCell className="text-[13px] text-muted-foreground py-2">
                    {video.updatedAt ? formatDate(video.updatedAt) : '—'}
                  </TableCell>

                  <TableCell className="text-[13px] text-muted-foreground py-2">
                    {(video.views || 0).toLocaleString(getInterfaceLanguage())}
                  </TableCell>
                  
                  <TableCell className="text-[13px] text-muted-foreground py-2">
                    {(video.plays || 0).toLocaleString(getInterfaceLanguage())}
                  </TableCell>

                  <TableCell className="text-[13px] text-muted-foreground py-2">
                    {(video.conversions || 0).toLocaleString(getInterfaceLanguage())}
                  </TableCell>

                  <TableCell className="text-[13px] text-muted-foreground py-2">
                    {formatCurrency(video.revenue || 0, video.currency || 'BRL')}
                  </TableCell>

                  <TableCell className="text-right py-2">
                    <VideoActionsDropdown
                      contentProps={{ className: 'w-48 z-50' }}
                      trigger={
                        <button
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center justify-center w-8 h-8 rounded-md hover:bg-black/[0.04] transition-colors"
                          aria-label={translateInterfaceText("Ações do vídeo")}
                        >
                          <MoreVertical className="h-4 w-4 text-[#98A2B3]" />
                        </button>
                      }
                    >
                        <DropdownMenuItem 
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/personalizations/${video.id}`);
                          }}
                        >
                          <Settings className="mr-2 h-4 w-4" />
                          {translateInterfaceText("Personalizar")}</DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={(e) => {
                            e.stopPropagation();
                            setVideoToRename(video);
                            setRenameModalOpen(true);
                          }}
                        >
                          <FileEdit className="mr-2 h-4 w-4" />
                          {translateInterfaceText("Renomear")}</DropdownMenuItem>
                        <DuplicateVideoMenuItem
                          videoId={video.id}
                          status={video.status}
                          isShared={Boolean(video.sharedId && video.sharedId !== '0' && video.sharedId !== video.id)}
                        />
                        <DropdownMenuItem 
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/metrics?videoId=${video.id}`);
                          }}
                        >
                          <BarChart3 className="mr-2 h-4 w-4" />
                          {translateInterfaceText("Ver Métricas")}</DropdownMenuItem>
                        <DropdownMenuSeparator />
                        {!(video.sharedId && video.sharedId !== '' && video.sharedId !== video.id) && (
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              if (video.thumbnailUrl) {
                                downloadVideoDirectLink(video.thumbnailUrl, video.title);
                              } else {
                                toast.error(translateInterfaceText("URL do vídeo não disponível"));
                              }
                            }}
                          >
                            <Download className="mr-2 h-4 w-4" />
                            {translateInterfaceText("Download")}</DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            setVideoToCopy(video);
                            setCopyCodeModalOpen(true);
                          }}
                        >
                          <Code2 className="mr-2 h-4 w-4" />
                          {translateInterfaceText("Copiar código")}</DropdownMenuItem>
                        {!(video.sharedId && video.sharedId !== '' && video.sharedId !== video.id) && (
                          <DropdownMenuItem
                            onClick={async (e) => {
                              e.stopPropagation();
                              if (!user?.id_user) {
                                toast.error(translateInterfaceText("Usuário não autenticado"));
                                return;
                              }
                              try {
                                const res = await shareVideoService.share(user.id_user, video.id);
                                if (res.success) {
                                  navigator.clipboard.writeText(res.data.link);
                                  toast.success(translateInterfaceText("Link de compartilhamento criado e copiado!"));
                                } else {
                                  toast.error(res.message || translateInterfaceText("Erro ao compartilhar"));
                                }
                              } catch (err: any) {
                                const msg = err?.response?.data?.message || err?.message || 'Erro ao compartilhar vídeo';
                                toast.error(msg);
                              }
                            }}
                          >
                            <Share2 className="mr-2 h-4 w-4" />
                            {translateInterfaceText("Compartilhar")}</DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            setVideoToDelete(video);
                            // Pequeno delay para garantir que o DropdownMenu fecha primeiro
                            setTimeout(() => {
                              setDeleteModalOpen(true);
                            }, 100);
                          }}
                          className="text-red-600 data-[highlighted]:bg-red-600 data-[highlighted]:text-white"
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          {translateInterfaceText("Excluir")}</DropdownMenuItem>
                    </VideoActionsDropdown>
                  </TableCell>
                </DraggableTableRow>
              ))
            )}
          </TableBody>
        </Table>
        </div>
      </div>
      
      {/* Modal de confirmação de exclusão */}
      <DeleteVideoModal
        open={deleteModalOpen}
        onOpenChange={(open) => {
          setDeleteModalOpen(open);
          if (!open) {
            // Limpar estado quando o modal fecha
            setTimeout(() => {
              setVideoToDelete(null);
            }, 100);
          }
        }}
        videoTitle={videoToDelete?.title || ''}
        onConfirm={async () => {
          if (videoToDelete) {
            onVideoDelete?.(videoToDelete);
            setDeleteModalOpen(false);
          }
        }}
      />
      
      {/* Modal de renomear */}
      <RenameVideoModal
        open={renameModalOpen}
        onOpenChange={setRenameModalOpen}
        currentTitle={videoToRename?.title || ''}
        onConfirm={async (newTitle) => {
          if (videoToRename) {
            try {
              // Chamar o serviço para atualizar o vídeo no backend
              await videoService.updateVideo(videoToRename.id, { nomeVideo: newTitle });

              // Se existe callback externo, chamar
              if (onVideoRename) {
                onVideoRename(videoToRename.id, newTitle);
              }

              toast.success(translateInterfaceText("Vídeo renomeado com sucesso!"));
              setRenameModalOpen(false);
              setVideoToRename(null);
            } catch (error) {
              toast.error(translateInterfaceText("Erro ao renomear vídeo"));
              console.error('Erro ao renomear:', error);
            }
          }
        }}
      />
      
      {/* Modal de copiar código */}
      {videoToCopy && (
        <CopyCodeModal
          isOpen={copyCodeModalOpen}
          onClose={() => {
            setCopyCodeModalOpen(false);
            setVideoToCopy(null);
          }}
          entityType="video"
          entityId={videoToCopy.id}
          entityName={videoToCopy.title}
          userId={user?.id_user}
          videoOrigin="ORIGINAL"
          hideJsTab={false}
        />
      )}

      {/* Barra de ações em massa */}
      {selectedVideoIds.size > 0 && (
        <BulkActionsBar
          selectedCount={selectedVideoIds.size}
          onClear={handleClearSelection}
          onDelete={() => setBulkDeleteModalOpen(true)}
        />
      )}

      {/* Modal de confirmação de exclusão em massa */}
      {bulkDeleteModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setBulkDeleteModalOpen(false)}
          />
          <div className="relative bg-white rounded-[16px] p-6 w-[420px] max-w-[90vw] shadow-[0_20px_60px_rgba(0,0,0,0.2)] animate-in fade-in zoom-in-95 duration-200">
            <h3 className="text-lg font-semibold text-[#1A1A1A] mb-2">
              {translateInterfaceText("Excluir vídeos selecionados")}</h3>
            <p className="text-sm text-[#6B7280] mb-6">
              {translateInterfaceText("Tem certeza que deseja excluir")}{" "}{selectedVideoIds.size} {" "}{translateInterfaceText("vídeo(s)? Esta ação não pode ser desfeita.")}</p>
            <div className="flex justify-end gap-3">
              <Button 
                variant="outline" 
                onClick={() => setBulkDeleteModalOpen(false)}
                className="px-4"
              >
                {translateInterfaceText("Cancelar")}</Button>
              <Button
                onClick={() => {
                  onBulkDelete?.(Array.from(selectedVideoIds));
                  handleClearSelection();
                  setBulkDeleteModalOpen(false);
                  toast.success(translateInterfaceText("{value0} vídeo(s) excluído(s) com sucesso", { value0: (selectedVideoIds.size) }));
                }}
                className="bg-red-600 hover:bg-red-700 text-white px-4"
              >
                {translateInterfaceText("Excluir")}{" "}{selectedVideoIds.size} {" "}{translateInterfaceText("vídeo(s)")}</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
